/**
 * English.
 *
 * Typed as `Messages`, which is derived from the French file, so a key added
 * there is a compile error here until it is supplied. That is the whole
 * mechanism: no key can go missing quietly, and nobody has to remember to
 * check.
 *
 * The workflow state names are left in French deliberately. They are the terms
 * the business uses and they appear on the document itself; an English reader
 * of this application still says "en revue" about a report, and translating
 * them would produce two vocabularies for one thing.
 */

import type { Messages } from '@/i18n/fr'

export const en: Messages = {
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    duplicate: 'Duplicate',
    done: 'Done',
    add: 'Add',
    remove: 'Remove',
    back: 'Back',
    send: 'Send',
    sending: 'Sending',
    loading: 'Loading...',
    problem: 'Something went wrong.',
    unreachable: 'The server could not be reached.',
    saveFailed: 'This report could not be saved.',
    tokenUnreadable: 'This access token could not be read.',
    tokenExpired: 'This access token has already expired.',
    reportUnavailable: 'This report could not be opened.',
  },

  templates: {
    title: 'New report',
    lead: 'What to start from. A template is a starting point: everything it lays down is yours from then on, and nothing links back to it.',
    create: 'Create',
    creating: 'Creating...',

    reportings: {
      title: 'CSRD reportings',
      lead: 'The first one picked is the principal, the report is about it. The others are secondary.',
      loading: 'Reaching CSRD...',
      unavailable:
        'CSRD could not be reached, or has no reporting. The report will be made without one.',
      n1: 'N-1: {code}',
      principal: 'Principal',
      secondaire: 'Secondary',
      makePrincipal: 'Make principal',
      required: 'Pick at least one reporting.',
    },

    previous: {
      name: 'Start from last year',
      about:
        "Last year's report as it is, tied to this year's reportings, with this year's figures read in from CSRD.",
    },

    previousPick: {
      title: "Last year's report",
      none: 'Choose a report',
      required: "Choose last year's report.",
      failed: 'That report could not be opened.',
      n1: 'The principal reporting follows {code} in CSRD.',
      principal: 'The principal reporting {from} becomes {to}.',
      matchFor: 'Secondary reporting standing for {code}',
      noMatch: 'None',
      orphans:
        'Tables tied to {codes} stay tied to it, and have to be reconnected by importing them again.',
    },

    pdf: {
      name: 'Start from a PDF',
      about:
        "An existing report's pages, as sections you can edit or exactly as printed. Choose the file or drop it here.",
      choose: 'Choose a PDF',
    },

    blank: {
      name: 'Blank report',
      about:
        'A cover, a contents page and a first chapter. Enough to begin without starting from a blank page.',
    },

    financial: {
      name: 'Financial dossier',
      about:
        'The order a statutory file is bound in: statements, notes, signatures. The tables are laid down, never filled in.',
      item: 'Item',
      current: 'Current year',
      prior: 'Prior year',
      overview: 'Overview',
      overviewTitle: 'Highlights of the year',
      overviewBody:
        'Summarise what marked the year: how the business moved, significant transactions, events after the reporting date.',
      keyFigures: 'Key figures',
      statements: 'Financial statements',
      balanceSheet: 'Balance sheet',
      assets: 'Assets',
      liabilities: 'Equity and liabilities',
      incomeStatement: 'Income statement',
      income: 'Result for the year',
      cashFlow: 'Cash flow statement',
      cash: 'Cash flows',
      notes: 'Notes',
      basis: 'Accounting policies',
      basisBody:
        'State the framework applied, the measurement bases used, and any change of method against the prior year.',
      consolidationScope: 'Scope of consolidation',
      firstNote: 'Note 1',
    },

    financialSustainability: {
      name: 'Financial and sustainability report',
      about:
        'The annual financial report with the sustainability statement inside the management report: management, sustainability, vigilance, corporate governance, consolidated and parent company accounts.',
      item: 'Item',
      current: 'Year N',
      prior: 'Year N-1',
      attestationTitle: 'Statement by the persons responsible for the annual financial report',
      attestationBody:
        'We certify that, to the best of our knowledge, the financial statements have been prepared in accordance with the applicable accounting standards and give a true and fair view of the assets, financial position and results of the company and of all the undertakings included in the consolidation, that the management report gives a fair review of the development of the business, the results and the financial position of the company and of all the undertakings included in the consolidation together with a description of the principal risks and uncertainties they face, and that it has been prepared in accordance with the applicable sustainability reporting standards.',
      profile: 'Group profile',
      editorial: 'Editorial',
      editorialBody:
        "The chief executive's message: what marked the year, the results achieved and the course set for the years ahead. Signed with their name and role.",
      purpose: 'Corporate purpose',
      purposeBody:
        "The company's purpose, as written into its articles where it is, and what it commits the company to in practice.",
      keyFigures: 'Key figures',
      trends: 'Major trends',
      trendsBody:
        'The underlying shifts that bear on the business: demography, climate, regulation, what customers and employees expect. Every figure with its source.',
      valueCreation: 'Value creation model',
      valueCreationBody:
        'The resources drawn on, the activities, and the value created for each stakeholder. It is where the description of the business model that SBM-1 asks for begins.',
      valueChain: 'Value chain',
      valueChainBody:
        'The main actors upstream and downstream, and where the company sits between them.',
      organisation: 'Group structure',
      management: 'Management report',
      activity: 'Business and results for the year',
      highlights: 'Main developments in the year',
      highlightsBody:
        'The highlights by business and by region: contracts won or lost, acquisitions, disposals, services launched.',
      revenue: 'Consolidated revenue',
      revenueBody:
        'Its amount, its change, and how much of the change comes from scope, currency and like-for-like growth.',
      operatingResult: 'Operating profit',
      operatingResultBody:
        'Its amount and the main effects behind it. State the measure used (EBITDA, EBIT) and how it is defined.',
      netResult: 'Consolidated net profit attributable to the Group',
      netResultBody:
        'The bridge from operating profit to net profit: net finance costs, income tax, share of equity-accounted companies.',
      equity: 'Consolidated equity',
      equityBody: 'The amount at the year end and how it moved during the year.',
      investments: 'Capital expenditure',
      investmentsBody:
        'The amount invested, how it splits by type, and how much of it third parties financed.',
      netDebt: 'Consolidated net debt',
      netDebtBody:
        'How it is defined, its amount, how it moved, and the main financing transactions of the year.',
      research: 'Research and development',
      researchBody: 'The work carried out and the resources devoted to it.',
      parentAccounts: 'Parent company accounts',
      parentAccountsBody:
        "The parent company's results and how they differ from the consolidated accounts.",
      outlook: 'Outlook',
      outlookBody:
        'How the situation is expected to develop, and the targets announced for next year.',
      subsequentEvents: 'Significant events after the reporting date',
      subsequentEventsBody:
        'What happened between the year end and the date the accounts were approved that bears on the position.',
      fiveYears: 'Company results over the last five financial years',
      fiveYearsItem: 'Type of information',
      fiveYearsCapital: 'Financial position at the year end',
      fiveYearsOperations: 'Overall results of operations',
      fiveYearsPerShare: 'Results of operations per share',
      fiveYearsStaff: 'Employees',
      paymentSuppliers: 'Invoices received and unpaid at the reporting date that are past due',
      paymentCustomers: 'Invoices issued and unpaid at the reporting date that are past due',
      paymentDelay: 'Payment delay bands',
      days0: '0 days (for information)',
      days30: '1 to 30 days',
      days60: '31 to 60 days',
      days90: '61 to 90 days',
      days91: '91 days or more',
      daysTotal: 'Total',
      invoiceCount: 'Number of invoices concerned',
      invoiceAmount: 'Total amount of the invoices concerned',
      shareOfPurchases: 'Percentage of total purchases for the year',
      shareOfRevenue: 'Percentage of revenue for the year',
      internalControl: 'Internal control and risk management',
      controlFramework: 'Reference framework',
      controlFrameworkBody:
        'The internal control framework applied and the three lines model: operational management, the risk management and compliance functions, internal audit.',
      controlOrganisation: 'How it is organised',
      controlOrganisationBody:
        'The dedicated functions (risk management, internal control, audit, insurance, ethics, cybersecurity), who they report to and how they work with the governance bodies.',
      riskFactors: 'Risk factors',
      riskStrategic: 'Strategic risks',
      riskOperational: 'Operational risks',
      riskFinancial: 'Financial risks',
      riskCompliance: 'Legal and compliance risks',
      riskPeople: 'Human resources risks',
      sustainability: 'Sustainability report',
      general: 'General information',
      basisOfPreparation: 'Basis of preparation',
      bp1: 'BP-1 – General basis for preparation of sustainability statements',
      bp1Body:
        'The scope of consolidation used and any difference from that of the financial statements, how far the value chain is covered upstream and downstream, and any use of the options to omit information.',
      bp2: 'BP-2 – Disclosures in relation to specific circumstances',
      bp2Body:
        'The time horizons used, value chain estimates, sources of uncertainty, changes in method, and information incorporated by reference.',
      governance: 'Governance',
      gov1: 'GOV-1 – The role of the administrative, management and supervisory bodies',
      gov1Body:
        'Their composition, diversity and sustainability expertise, and how they oversee impacts, risks and opportunities.',
      gov2: "GOV-2 – Information provided to and sustainability matters addressed by the undertaking's administrative, management and supervisory bodies",
      gov2Body: 'Who informs them, how often, and which matters they considered during the year.',
      gov3: 'GOV-3 – Integration of sustainability-related performance in incentive schemes',
      gov3Body:
        'The sustainability criteria built into executive pay, and how much weight they carry.',
      gov4: 'GOV-4 – Statement on due diligence',
      gov4Body:
        'A mapping table pointing, for each step of due diligence, to where the report describes it.',
      gov5: 'GOV-5 – Risk management and internal controls over sustainability reporting',
      gov5Body:
        'The controls that make the collection and production of sustainability data reliable, the main risks found and the controls that answer them.',
      strategy: 'Strategy',
      sbm1: 'SBM-1 – Strategy, business model and value chain',
      sbm1Body:
        'Products, markets and headcount by region, the sustainability objectives, and the business model described along the value chain.',
      sbm2: 'SBM-2 – Interests and views of stakeholders',
      sbm2Body:
        'The key stakeholders, how the company engages with them, and how their expectations were taken into account.',
      sbm3: 'SBM-3 – Material impacts, risks and opportunities and their interaction with strategy and business model',
      sbm3Body:
        'The material impacts, risks and opportunities, where they arise in the value chain, their current and anticipated effects, and how resilient the strategy is.',
      iroManagement: 'Impact, risk and opportunity management',
      iro1: 'IRO-1 – Description of the process to identify and assess material impacts, risks and opportunities',
      iro1Body:
        'The double materiality assessment: sources, criteria and thresholds, stakeholders consulted, sign-off by governance. It is the first thing the assurance provider examines.',
      iro2: "IRO-2 – Disclosure requirements in ESRS covered by the undertaking's sustainability statement",
      iro2Body:
        'The disclosure requirements applied, with where each is found in the report, and those judged not material. The index in the annex is built from the references each section carries.',
      environmental: 'Environmental information',
      climateTarget: 'Greenhouse gas emissions reduction target',
      taxonomy: 'EU taxonomy',
      taxonomyIntro: 'Taxonomy-aligned activities',
      taxonomyIntroBody:
        'The share of turnover, capital expenditure (CapEx) and operating expenditure (OpEx) that is eligible and then aligned under Article 8 of Regulation (EU) 2020/852, and how it was calculated.',
      social: 'Social information',
      governanceInformation: 'Governance information',
      sustainabilityAnnexes: 'Annexes to the sustainability report',
      disclosureIndex: 'Index of disclosure requirements',
      otherEuLegislation: 'Datapoints that derive from other EU legislation',
      otherEuLegislationBody:
        'The list set out in Appendix B to ESRS 2, stating for each datapoint whether it is reported and where.',
      definitions: 'Definitions',
      certificationReport: 'Report on the certification of sustainability information',
      certificationReportBody:
        'The report of the statutory auditor or independent third party, with the conclusion reached and the level of assurance obtained.',
      vigilance: 'Vigilance plan',
      vigilanceIntro: 'Scope and approach',
      vigilanceIntroBody:
        'The companies covered, the suppliers and subcontractors concerned, and how the plan was drawn up with stakeholders.',
      vigilanceMapping: 'Risk mapping',
      vigilanceHumanRights: 'Human rights and fundamental freedoms',
      vigilanceHealthSafety: 'Health and safety of people',
      vigilanceEnvironment: 'Environment',
      vigilanceAssessment: 'Regular assessment of subsidiaries, subcontractors and suppliers',
      vigilanceAssessmentBody:
        'How the position of subsidiaries and business partners is assessed against the risk mapping, and how often.',
      vigilanceMitigation: 'Actions to mitigate risks and prevent serious harm',
      vigilanceMitigationBody:
        'The actions taken for each priority risk identified in the mapping.',
      vigilanceAlert: 'Alert and reporting mechanism',
      vigilanceAlertBody:
        'The channel open to employees and third parties, set up together with the representative trade unions, and how the reports received were handled.',
      vigilanceMonitoring: 'Monitoring of measures and assessment of their effectiveness',
      vigilanceMonitoringBody:
        'The indicators used to follow how the plan is implemented and how well it works.',
      vigilanceReport: 'Report on implementation',
      vigilanceReportBody: 'What was done during the year, for each measure in the plan.',
      corporateGovernance: 'Corporate governance report',
      boardComposition: 'Governance structure and composition',
      boardMember: 'Member',
      boardRole: 'Role',
      boardIndependent: 'Independent',
      boardCommittees: 'Committees',
      boardFunctioning: 'How the governance bodies operate',
      boardFunctioningBody:
        'How often they meet, attendance, the work of the committees and the board evaluation.',
      diversity: 'Diversity policy',
      diversityBody:
        'The policy applied to the make-up of the board and the executive bodies, its objectives and its results, in particular on the balanced representation of women and men.',
      financialControl: 'Internal control over financial reporting',
      financialControlBody:
        'The main features of the internal control and risk management procedures for preparing and processing accounting and financial information.',
      consolidated: 'Consolidated financial statements',
      auditorsConsolidated: "Statutory auditors' report on the consolidated financial statements",
      auditorsConsolidatedBody:
        'The opinion, the basis for it, the justification of assessments and the key audit matters.',
      comprehensiveIncome: 'Statement of comprehensive income',
      consolidatedBalanceSheet: 'Consolidated balance sheet',
      consolidatedCashFlows: 'Consolidated statement of cash flows',
      changesInEquity: 'Consolidated statement of changes in equity',
      equityItem: 'Movements',
      shareCapital: 'Share capital',
      reserves: 'Reserves',
      netIncome: 'Net profit',
      totalEquity: 'Total equity',
      consolidatedNotes: 'Notes to the consolidated financial statements',
      consolidatedNotesBody:
        'The framework applied, accounting policies, significant judgements and estimates, then the notes on each line item.',
      firstNote: 'Note 1',
      parent: 'Parent company financial statements',
      auditorsParent: "Statutory auditors' report on the annual financial statements",
      auditorsParentBody:
        "The opinion on the parent company's annual financial statements, the basis for it and the justification of assessments.",
      parentBalanceSheet: 'Balance sheet',
      parentIncomeStatement: 'Income statement',
      parentNotes: 'Notes to the parent company financial statements',
    },

    csrd: {
      name: 'Sustainability statement',
      about:
        'The structure the ESRS set out: general disclosures, then environment, social and governance.',

      general: 'ESRS 2 \u2013 General disclosures',
      basis: 'Basis of preparation',
      basisBody:
        'State the scope of the statement, how it is consolidated, and whether it covers the upstream and downstream value chain.',
      governance: 'Governance',
      governanceBody:
        'Describe the role of the administrative, management and supervisory bodies: composition, sustainability expertise, what they are told and when.',
      strategy: 'Strategy and business model',
      strategyBody:
        'Set out the business model, the markets served, and where sustainability sits in the strategy.',
      stakeholders: 'Stakeholders',
      stakeholdersBody:
        'Who the stakeholders are, how they are engaged, and what their views changed about the strategy.',
      iros: 'Impacts, risks and opportunities',
      irosBody:
        'The material impacts, risks and opportunities identified, and how they interact with the strategy and business model.',
      assessment: 'Double materiality assessment',
      assessmentBody:
        'Describe the process followed: sources, thresholds, stakeholders consulted, how it was approved. This process is the first thing an assurance provider examines.',
      covered: 'Disclosures covered',
      coveredBody:
        'The disclosure requirements covered, those assessed as not material, and those deferred under the transitional provisions.',

      environment: 'Environment',
      social: 'Social',
      governanceTopic: 'Governance',
      annex: 'Annexes',

      topics: {
        e1: 'E1 \u2013 Climate change',
        e2: 'E2 \u2013 Pollution',
        e3: 'E3 \u2013 Water and marine resources',
        e4: 'E4 \u2013 Biodiversity and ecosystems',
        e5: 'E5 \u2013 Resource use and circular economy',
        s1: 'S1 \u2013 Own workforce',
        s2: 'S2 \u2013 Workers in the value chain',
        s3: 'S3 \u2013 Affected communities',
        s4: 'S4 \u2013 Consumers and end-users',
        g1: 'G1 \u2013 Business conduct',
      },

      climateTarget: 'Emissions reduction target',
      materialityTitle: 'Materiality',
      materialityBody:
        'Whether this topic is material, and why. A topic assessed and found not material has to be said so: leaving it out silently is not an omission, it is a failure. Then tag this section with its ESRS reference.',
      policies: 'Policies',
      policiesBody:
        'The policies adopted, their scope, who answers for them and how they are made public.',
      actions: 'Actions',
      actionsBody:
        'The actions taken, the resources allocated to them and the horizon they run to.',
      targets: 'Targets',
      targetsBody: 'The measurable targets, their base year, their deadline and the progress made.',
      metrics: 'Metrics',

      assuranceTitle: 'Assurance report',
      assuranceBody:
        'The practitioner conclusion, the level of assurance obtained and the scope of the work performed.',
      definitions: 'Definitions',
    },
  },

  workspace: {
    product: 'Report builder',
    title: 'Reports',
    subtitle: 'Every report you can reach, and where each one stands.',
    create: 'New report',
    emptyTitle: 'No reports yet',
    empty: 'Start one with New report.',
    loading: 'Loading your reports...',
    deleteOne: 'Delete {name}',
    invitationPending: 'Invitation pending',
    untitled: 'Untitled',
  },

  access: {
    menu: 'Access',
    title: 'Access',
    subtitle: 'Who may do what, and for which direction. A change applies at their next request.',
    back: 'Back to reports',
    notAllowed: 'Only an administrator can change who holds what.',
    addLabel: 'Add a person',
    addHint:
      'The identifier the portal shows for the account. Name and email are optional here: until the portal can say who they are, they are what the other screens show.',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    edit: 'Edit',
    editOne: 'Edit {name}',
    save: 'Save',
    cancel: 'Cancel',
    addPlaceholder: 'For example 1058',
    add: 'Add',
    empty: 'Nobody has been given a profile yet. Add a person by their identifier.',
    tableCaption: 'People and what they hold',
    person: 'Person',
    profiles: 'Profiles',
    directions: 'Directions',
    lastSeen: 'Last sign-in',
    neverSeen: 'Not signed in yet',
    grantedBy: 'Set by {name}',
    subject: 'ID {sub}',
    actions: 'Actions',
    remove: 'Take back everything granted to {name}',
    removeShort: 'Remove',
    level: {
      none: 'No access',
      read: 'Consult',
      write: 'Modify',
    },
    profile: {
      RFA_USER: 'User',
      RFA_VALIDEUR: 'Validator',
      RFA_ADMIN: 'Administrator',
    },
  },

  csrdLink: {
    title: 'Linked to CSRD',
    locked:
      'Figures and labels come from CSRD and cannot be edited here. Change them in CSRD, then refresh. How the table looks is still yours to set.',
    facts: {
      entity: 'Entity',
      reporting: 'Reporting',
      disclosure: 'Disclosure',
      read: 'Last read',
      status: 'CSRD status',
    },
    status: 'Collection {collected} · Control {controlled} · Audit {audited}',
    statusHint: 'How far the collection, its control and its audit have come, as CSRD numbers it.',
    refresh: 'Refresh from CSRD',
    state: {
      checking: 'Checking CSRD...',
      current: 'Up to date with CSRD.',
      stale: '{count} cell differs from CSRD. | {count} cells differ from CSRD.',
      layout: 'The layout of the table changed in CSRD.',
      refreshing: 'Reading CSRD...',
    },
    result: {
      nothing: 'Nothing had changed in CSRD.',
      updated: '{count} cell updated, {date}. | {count} cells updated, {date}.',
      reshaped:
        'The layout changed in CSRD, so the table was laid out again. Its look is kept on the rows and columns still there.',
      undo: 'Undo brings the previous figures back.',
    },
    banner: {
      changed:
        'CSRD has changed since these figures were read ({date}): {count} table affected. | CSRD has changed since these figures were read ({date}): {count} tables affected.',
      refresh: 'Refresh',
      later: 'Later',
      blocked:
        'CSRD is not on the principal reporting of this report ({code}), so its tables cannot be checked.',
      switch: 'Move CSRD to {code}',
    },
    errors: {
      otherReporting: 'CSRD is on another reporting. Choose {code} in CSRD, then try again.',
      gone: 'CSRD no longer has this table.',
      detached:
        'This table comes from reporting {code}, which this report no longer has. Import it again to reconnect it.',
      notEditable:
        'This report cannot be edited in its present state, so its figures cannot be refreshed.',
    },
  },

  session: {
    connecting: 'Taking you to the portal...',
    blockedTitle: 'Cannot connect',
    unreachableBody:
      'Connecting to the portal did not work. Check that it is running, then try again.',
    hostSilent: 'The page holding this one passed on no session. Reload it, then try again.',
    retry: 'Try again',
    callbackWorking: 'Signing you in...',
    callbackFailed:
      'The sign-in could not be completed. Close this tab and open the application again.',
    notFoundTitle: 'Nothing here',
    notFoundBody: 'This address does not match anything in the application.',
    notFoundBack: 'Back to your reports',
  },

  bar: {
    reportName: 'Report name',
    backToReports: 'Back to your reports',
    saved: 'Saved',
    saving: 'Saving',
    unsaved: 'Unsaved changes',
    notSaved: 'Not saved',
    undo: 'Undo',
    redo: 'Redo',
    comments: 'Comments',
    share: 'Share',
    export: 'Export',
    history: 'History',
  },

  structure: {
    views: 'Document views',
    outline: 'Outline',
    contents: 'Contents',
    contentsEmpty: 'No headings yet. Chapters and headings will show up here.',
    contentsPage: 'p. {page}',
    contentsPages: '{count} page | {count} pages',
    untitled: 'Untitled',
    goTo: 'Go to “{title}”',
  },

  selection: {
    title: '{count} section selected | {count} sections selected',
    clear: 'Deselect',
    more: '{count} more section | {count} more sections',
    duplicate: 'Duplicate',
    duplicateEnd: 'Duplicate at the end of the document',
    moveStart: 'Move to the start',
    moveEnd: 'Move to the end',
    delete: 'Delete',
    hint: 'Ctrl or Cmd and a click adds a row, Shift and a click picks everything between. Drag one of them to move them all. One undo takes the whole run back.',
  },

  versions: {
    open: 'Versions',
    title: 'Versions of this report',
    keepTitle: 'Keep this version',
    namePlaceholder: 'A name (optional)',
    commentPlaceholder: 'A few words about this version (optional)',
    keep: 'Keep this version',
    keepHint:
      'Keeps the content of the report as it is now, to go back to it or to see what changed since.',
    empty: 'No version kept yet. One is also kept each time the report changes state.',
    number: 'V{number}',
    by: 'by {author}',
    auto: 'Automatic',
    locked: 'Locked: taken once the report was validated',
    reason: {
      manual: 'Version kept',
      other: 'Automatic version',
      restore: 'Before going back to {from}',
      state: {
        brouillon: 'Reopened',
        en_revue: 'Sent for review',
        valide: 'Validated',
        diffuse: 'Issued',
        rejete: 'Rejected',
      },
    },
    compare: 'Compare',
    compareLatest: 'Compare with the latest version',
    restore: 'Restore',
    confirmRestore: 'Restore V{number}? What the report says now is kept as a version first.',
    confirm: 'Confirm',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Delete this version?',
    cannotRestore: 'A report under review or validated cannot be changed: reopen it to go back.',
    bar: {
      title: 'Changes',
      against: 'Compared with V{number}, {date}',
      count: '{count} section changed | {count} sections changed',
      none: 'Nothing changed since this version.',
      list: 'List',
      previous: 'Previous change',
      next: 'Next change',
      quit: 'Stop comparing',
      export: 'Export PDF',
      exporting: 'Exporting',
      exportFailed: 'The changes could not be exported.',
    },
    review: {
      accept: 'Accept this change',
      reject: 'Reject this change',
      rejectRemoval: 'Reject: bring the block back',
      acceptAll: 'Accept all',
      accepted: 'accepted',
      rejected: 'rejected',
      acceptedBy: 'Accepted by {author}, {when}',
      rejectedBy: 'Rejected by {author}, {when}',
      toRead: '{count} to read',
      allRead: 'Everything has been read.',
      unread:
        '{count} change since V{number} has not been read. | {count} changes since V{number} have not been read.',
    },
    delta: {
      title: 'Changes to {report}',
      version: 'V{number}, {date}',
      against: 'Compared with {against}',
      made: 'Made on {date} by {author}',
      nothing: 'Nothing changed since that version.',
      modified: 'section changed | sections changed',
      added: 'section added | sections added',
      removed: 'section removed | sections removed',
      moved: 'section moved | sections moved',
    },
    change: {
      added: 'Added',
      modified: 'Changed',
      moved: 'Moved',
      removed: 'Removed',
    },
    list: {
      title: 'What changed since V{number}',
      removedHint: 'Removed since this version',
      empty: 'Nothing changed since this version.',
    },
    cell: {
      title: 'Value changed since V{number}',
      titleLabel: 'Label changed since V{number}',
      titleNew: 'New since V{number}',
      source: 'CSRD',
      was: 'In V{number}',
      now: 'Now',
      gap: 'Change',
      read: 'Read from CSRD on',
      where: 'Source',
      history: 'See the history in CSRD',
      last: 'Last changed',
      open: 'Click to see the history of the cell',
      notes: '{count} value changed since V{number} | {count} values changed since V{number}',
    },
    cellPanel: {
      title: 'Cell history',
      cell: 'Cell',
      header: 'Header',
      was: 'Previous value',
      after: 'New value',
      afterVersion: 'After V{number}',
      gone: 'absent',
      empty: 'No change to this cell in the journal.',
    },
    detail: {
      title: 'Changes to this block',
      before: 'Before: V{number}',
      after: 'After: the report now',
      sideBySide: 'Side by side',
      merged: 'Merged',
      wordsAdded: '+{count} words',
      wordsRemoved: '-{count} words',
      paragraphs: '{touched} of {total} paragraphs touched',
      absent: 'This paragraph is not in this version',
      added: 'Added',
      removed: 'Removed',
      cells: '{count} cell changed | {count} cells changed',
      rowsAdded: '{count} row added | {count} rows added',
      rowsRemoved: '{count} row removed | {count} rows removed',
      fields: 'What was set differently',
      wasEmpty: 'nothing',
      addedBlock: 'This block was added after that version.',
      removedBlock: 'This block was in that version and is not in the report any more.',
      movedBlock: 'This block says the same, in another place.',
      unchanged: 'Nothing changed in this block.',
    },
  },

  journal: {
    tabs: { versions: 'Versions', journal: 'Who changed what' },
    trail: {
      empty: 'Nothing has been changed since the report was made.',
      more: 'Show older changes',
    },
    history: {
      open: 'History of this block',
      title: 'History of the block',
      latest: 'Latest',
      restore: 'going back to a version',
      empty: 'Nobody has changed this block since the report was made.',
      comparedTo: 'Compared with',
      previous: 'The change before',
      currentReport: 'The report now',
      before: 'Before: {author}, {when}',
      after: 'After: {author}, {when}',
      beforeAdded: 'Before: this block did not exist',
      original: 'Before: the block as it was before this change',
      noEarlier: 'No earlier state of this block is kept',
      saidThen: 'What it said, {when}',
      now: 'The report now',
      gone: 'The block is gone',
      putBack: 'Put the block back as it was after this change',
      putBackBefore: 'Put the block back as it was before this change',
      bringBack: 'Bring the block back as it was after this change',
      replaced: 'Done. The block is as it was; undo brings back what it says now.',
      returned: 'Done. The block is back at the end of the report; undo removes it again.',
      noContent: 'What the block said then is not kept: it was too large to copy at each change.',
    },
  },

  outline: {
    sections: 'Sections',
    reportOutline: 'Report outline',
    empty: 'Nothing here yet. Add a section from the panel on the right.',
    expand: 'Expand',
    collapse: 'Collapse',
    openCount: '{count} open',
  },

  palette: {
    title: 'Add',
    dragHint: 'Click to add after the selection, or drag onto the page to place it.',
    addSection: 'Add a section',
    importFrom: 'Take from a document',
    word: 'Word',
    excel: 'Excel',
    pdf: 'PDF',
    pdfHint: 'Bring in the pages of a PDF report as editable sections, or exactly as printed.',
    wordHint:
      'Take the headings, paragraphs and tables of a Word document, choosing which of them.',
    excelHint: 'Take a range of cells from a workbook, as a table.',
    csrd: 'CSRD',
    csrdHint: 'Take disclosures collected in CSRD, with their narratives and their tables.',
    bands: {
      structure: 'Structure',
      content: 'Content',
      annex: 'Annex',
      sustainability: 'Sustainability',
      frontAndBack: 'Front and back',
    },
    hints: {
      group: 'A chapter that holds other sections',
      heading: 'A title within the document',
      pageBreak: 'Start what follows on a new page',
      layer: 'Text, images and shapes placed freely on the page',
      imagePage: 'A single picture, on its own page',
      fixedPage: 'A page imported from a PDF, exactly as printed',
      text: 'Prose, with formatting',
      table: 'Rows and columns of figures',
      kpi: 'A row of headline numbers',
      image: 'A picture or a chart',
      cover: 'The front page',
      contents: 'Generated from the headings',
      chart: 'A chart drawn from figures you type',
      note: 'A numbered note of the annex',
      scope: 'The consolidated entities and their method',
      glossary: 'Terms and what they mean',
      divider: 'A rule, or a measured gap',
      datapoint: 'A reported figure, with its unit, its comparative and its method',
      target: 'A measurable target, and the progress that follows from it',
      materiality: 'The double materiality matrix',
      iro: 'The table of impacts, risks and opportunities',
      risks: 'The principal risks, what they are and how they are managed',
      ghg: 'Scopes 1, 2 and 3, with subtotals that cannot be typed',
      taxonomy: 'Turnover, CapEx and OpEx, eligible and aligned',
      disclosureIndex: 'The index built from the ESRS references in the document',
      signatures: 'Who signs, and where',
    },
  },

  sections: {
    cover: 'Cover',
    group: 'Group',
    heading: 'Heading',
    text: 'Text',
    table: 'Table',
    image: 'Image',
    kpi: 'Key figures',
    chart: 'Chart',
    note: 'Note',
    scope: 'Scope',
    glossary: 'Glossary',
    divider: 'Divider',
    datapoint: 'Datapoint',
    target: 'Target',
    materiality: 'Materiality',
    iro: 'IRO',
    risks: 'Risks',
    ghg: 'GHG inventory',
    taxonomy: 'Taxonomy',
    disclosureIndex: 'ESRS index',
    contents: 'Contents',
    signatures: 'Signatures',
    pageBreak: 'Page break',
    layer: 'Free placement',
    imagePage: 'Full-page image',
    fixedPage: 'Imported page',
  },

  newSection: {
    cover: 'Cover',
    group: 'New group',
    heading: 'New heading',
    text: 'Text',
    table: 'New table',
    image: 'Image',
    kpi: 'Key figures',
    contents: 'Contents',
    signatures: 'Signatures',
    pageBreak: 'Page break',
    layer: 'Free placement',
    imagePage: 'Full-page image',
    fixedPage: 'Imported page',
    chart: 'Chart',
    note: 'New note',
    scope: 'Scope of consolidation',
    glossary: 'Glossary',
    divider: 'Divider',
    datapoint: 'New datapoint',
    target: 'New target',
    materiality: 'Double materiality assessment',
    iro: 'Impacts, risks and opportunities',
    risks: 'Risk factors',
    ghg: 'Greenhouse gas inventory',
    taxonomy: 'EU taxonomy',
    disclosureIndex: 'Index of disclosures',
    firstChapter: 'Chapter 1',
  },

  devAccount: {
    title: 'Act as another account (development)',
    hint: 'Development only: this stands in for signing in. Pick who this tab acts as.',
    fixed: 'Development user',
    none: 'No account to offer. Add people on the Access screen, with a token in the secrets folder.',
    expired: 'token expired',
  },

  canvas: {
    empty:
      'An empty report. Add a section from the panel on the right, or import one from Word or Excel.',
    groupEmpty:
      'This group is empty. Drop sections into it in the outline, or add one while it is selected.',
    masked: 'Restricted content',
    zoom: 'Zoom',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    view: 'Page view',
    viewSheets: 'Separate pages',
    viewContinuous: 'Continuous scroll',
    blankSheet: 'Blank page',
    fold: 'Continues overleaf',
    spread: 'Double page',
    layer: {
      none: 'no elements',
      one: '1 element',
      many: '{count} elements',
      textEmpty: 'Text',
      imageEmpty: 'Image',
      handle: 'Resize',
      turn: 'Rotate: hold Shift to turn by 15°',
      drop: 'Drop the image to place it here',
      dropRefused: {
        notAnImage: 'This file is not an image.',
        tooLarge: 'This image is larger than 4 MB.',
        unreadable: 'This image could not be read.',
      },
    },
    foldHint:
      'Whatever runs past this line prints on another page. A page break lets you choose where that page starts.',
    comment: 'Comment on this section',
    openDiscussion: 'Open the discussion on this section',
    dragToMove: 'Drag to move',
    emptyText: 'Empty',
    noImage: 'No image chosen',
    contentsEmpty: 'Nothing to list yet. Headings and groups appear here.',
    defaults: {
      datapoint: 'Reported figure',
      target: 'Target',
      subtitle: 'Consolidated annual accounts',
      standard: 'Accounting framework',
      note: 'Foot line: reference, version, confidentiality',
      logo: 'Your logo',
      framework: 'IFRS',
      assurance: 'Limited assurance',
      entity: 'Entity',
      period: 'Period',
      name: 'Name',
    },
  },

  editor: {
    placeholder: 'Write here',
    formatting: 'Formatting',
    bold: 'Bold',
    italic: 'Italic',
    strike: 'Strikethrough',
    underline: 'Underline',
    superscript: 'Superscript',
    subscript: 'Subscript',
    plain: 'Clear formatting',
    bulletList: 'Bulleted list',
    orderedList: 'Numbered list',
    link: 'Link',
    linkPrompt: 'Link address',
    textStyle: 'Text style',
    textStyleNone: 'No style',
  },

  document: {
    custom: 'Custom',
    amount: 'Value',
    width: 'Width',
    height: 'Height',
    marginTop: 'Top',
    marginSide: 'Sides',
    marginBottom: 'Bottom',
    bleed: 'Bleed',
    bleedHint:
      'How far background pictures run past the trimmed size, so a guillotine landing slightly off leaves no white sliver. Whatever is out there is lost: put nothing readable in it.',
    bleeds: {
      none: 'None',
      narrow: '3 mm',
      wide: '5 mm',
    },
    // ---------- The kind of dossier ----------
    tables: 'Tables',
    tableHead: 'Head',
    tableHeadHint: 'How the column names are drawn, in every table that does not set its own.',
    tableRule: 'Rules',
    tableRuleHint:
      'A printed statement is ruled across: the eye follows a row, it does not cross a column.',
    tableRules: {
      all: 'Gridded',
      horizontal: 'Across only',
      minimal: 'Minimal',
    },
    tableDensity: 'Row height',
    tableDensityHint: 'Room to breathe on a statement of sixty lines, or room to fit it.',
    tableDensities: {
      compact: 'Tight',
      normal: 'Normal',
      roomy: 'Open',
    },
    tableZebra: 'Banded rows',
    tableZebraHint: 'A very faint tint on every other row, for a table too wide to follow.',
    negativesInRed: 'Negatives in red',
    negativesInRedHint:
      'Black with parentheses is the convention: red does not survive a photocopier.',

    structure: 'Structure',
    headingNumbering: 'Numbering',
    headingNumberingHint:
      'Headings and chapters, numbered in the document and in the contents alike.',
    headingNumbers: {
      none: 'None',
      decimal: '1, 1.1',
      legal: 'I, A, 1',
    },
    chapterPageBreak: 'Chapter starts a new page',
    chapterPageBreakHint: 'When printed only: the page on screen is one continuous sheet.',
    contentsLeaders: 'Leader dots',
    contentsLeadersHint: 'The dots leading from a heading to its page number in the contents.',

    dossier: 'Financial dossier',
    dossierHint:
      'What the document states about itself. These appear on the cover and above the tables, so each is said once here rather than typed in both places.',
    framework: 'Framework',
    frameworkHint:
      'The accounting framework the statements are prepared under. Printed under the title on the cover.',
    frameworks: {
      none: 'Not stated',
    },
    currency: 'Currency',
    currencyHint:
      'What the figures are in. A French reader of a Tunisian subsidiary still sees dinars.',
    currencies: {
      none: 'None',
    },
    figureScale: 'Scale',
    figureScaleHint:
      'Stated above every table. No figure is recalculated: this says what they are, it does not convert them.',
    figureScales: {
      none: 'Not stated',
      units: 'Units',
      thousands: 'Thousands',
      millions: 'Millions',
    },
    scales: {
      units: 'In {currency}',
      thousands: 'In thousands of {currency}',
      millions: 'In millions of {currency}',
    },
    scalesBare: {
      units: 'In units',
      thousands: 'In thousands',
      millions: 'In millions',
    },

    sustainability: 'Sustainability (CSRD / ESG)',
    sustainabilityHint:
      'One volume can be prepared under an accounting framework and a sustainability framework at once, so the two are said separately.',
    esgFramework: 'Sustainability framework',
    esgFrameworkHint: 'Printed on the cover, alongside the level of assurance.',
    assurance: 'Assurance',
    assuranceHint: 'What an auditor has said about it. Nothing is printed until this is set.',
    assurances: {
      none: 'None',
      limited: 'Limited assurance',
      reasonable: 'Reasonable assurance',
    },

    houseStyle: 'House style',
    houseStyleHint:
      'Colours, typefaces, tables and charts set together, tried on a preview and applied in one go. Once saved, a house style is there for your next reports.',
    houseStyleOpen: 'Open the house style',
    tableStyles: 'Table styles',
    tableStylesHint:
      'The library of ways to draw a table, shared across the firm. Make and change them here, then choose one on each table instead of setting every table by hand.',
    tableStylesOpen: 'Open the table styles',
    specimenFigures: {
      revenue: 'Revenue, EUR m',
      result: 'Net income, EUR m',
      emissions: 'Emissions per passenger',
    },
    specimenChart: {
      title: 'Revenue by activity',
      transport: 'Transport',
      subsidiaries: 'Subsidiaries',
      services: 'Services',
    },
    specimen: 'Specimen of the settings',
    specimenChapter: 'Consolidated accounts',
    specimenSection: 'Current assets',
    specimenBody:
      'This sheet is not part of the report. It holds one example of each thing these settings govern, because a cover page has no table, no numbered heading and no contents on it.',
    specimenContents: 'Contents',
    specimenContentsLines: {
      one: 'Directors report',
    },
    specimenTable: {
      item: 'Item',
      cash: 'Cash',
      debtors: 'Trade debtors',
      provisions: 'Provisions',
      total: 'Total current assets',
    },

    title: 'Layout',
    fromHere: 'How the report is set: its paper, its margins and its type.',
    open: 'Open the layout',
    backToReport: 'Back to the report',
    preview: 'Preview',
    previewEmpty: 'Nothing to preview yet.',
    paper: 'Paper',
    pageSize: 'Size',
    pageSizeHint: 'The paper the report is printed on.',
    margin: 'Margins',
    marginHint: 'How much white space surrounds the text.',
    text: 'Text',
    textSize: 'Size',
    textSizeHint: 'How large the body text is set, in points.',
    alignment: 'Alignment',
    alignmentHint:
      'Justified as a printed report is set, or ragged right where hyphenation does not suit.',
    colour: 'Colours',
    paperColour: 'Paper',
    paperColourHint: 'White, or cream as a printed annual report is.',
    accent: 'Accent colour',
    accentHint: 'The document rules: cover, table headers, key figures.',
    accentPick: 'Pick a colour',
    accentHex: 'Colour in hexadecimal',
    accentInvalid: 'Six hexadecimal digits, for example 1c3f6e.',
    accentHeadings: 'Headings in colour',
    accentHeadingsHint: 'Sets the document headings in the accent colour.',
    papers: {
      white: 'White',
      cream: 'Cream',
    },
    accents: {
      ink: 'Ink',
      navy: 'Navy',
      teal: 'Teal',
      forest: 'Forest',
      burgundy: 'Burgundy',
      bronze: 'Bronze',
    },
    typeface: 'Typeface',
    typefaceHint: 'What the report is set in, on screen and in the export.',
    sample: 'Operating result',
    storedWithReport:
      'Saved with the report rather than with your browser: everyone sees the same document, and the PDF matches the screen.',
    pageSizes: {
      a4: 'A4',
      letter: 'Letter',
    },
    margins: {
      narrow: 'Narrow',
      normal: 'Normal',
      wide: 'Wide',
    },
    textSizes: {
      small: 'Small',
      normal: 'Normal',
      large: 'Large',
    },
    alignments: {
      justified: 'Justified',
      ragged: 'Ragged right',
    },
    kinds: {
      sans: 'sans serif',
      serif: 'serif',
    },
    typefaces: {
      inter: 'Inter',
      'source-serif': 'Source Serif',
      lora: 'Lora',
      'plex-sans': 'IBM Plex Sans',
      garamond: 'EB Garamond',
    },
    textStylesGroup: 'Text styles',
    textStylesHint:
      'A colour and a typeface, under a name, to apply to a selection from the text toolbar. Not free CSS: only a colour and a typeface, for the same reasons the accent colour above is restricted.',
    textStyles: {
      empty: 'No styles yet.',
      name: 'Style name',
      typeface: 'Style typeface',
      remove: 'Remove',
      add: '+ Add a style',
      sample: 'Sample text',
      newName: 'Style {count}',
    },
  },

  fontLibrary: {
    title: 'Fonts',
    open: 'Add fonts',
    openHint: 'Google Fonts or your own files',
    missing: 'Font not found',
    sample: 'Preview text',
    sampleDefault: 'Annual report 2026: EUR 1,284,605',
    tabs: {
      library: 'Library',
      google: 'Google Fonts',
      upload: 'Upload',
    },
    sources: {
      google: 'Google Fonts',
      upload: 'Uploaded',
    },
    categories: {
      'sans-serif': 'Sans serif',
      serif: 'Serif',
      display: 'Display',
      handwriting: 'Handwriting',
      monospace: 'Monospace',
    },
    allCategories: 'All',
    weights: {
      100: 'Thin',
      200: 'Extra light',
      300: 'Light',
      400: 'Regular',
      500: 'Medium',
      600: 'Semibold',
      700: 'Bold',
      800: 'Extra bold',
      900: 'Black',
    },
    weightRange: '{from} to {to}',
    italicOf: '{weight} italic',
    styles: '{count} style | {count} styles',
    library: {
      empty:
        'The library is empty. Add a family from Google Fonts, or upload the files of your house typeface.',
      browse: 'Browse Google Fonts',
      by: 'added by {owner}',
      rename: 'Rename',
      renameSave: 'Save',
      addStyles: 'Add styles',
      remove: 'Delete the family "{name}"',
      removeHint: 'Reports set in it will fall back on the default typeface.',
      removeYes: 'Delete',
      removeNo: 'Keep',
    },
    google: {
      search: 'Search for a family',
      kind: 'Kind',
      note: 'A family you add is downloaded once by the server and then served by the application: browsers never contact Google. All of them are under open licences (OFL or Apache) and may be embedded in PDFs.',
      results: '{count} family | {count} families',
      none: 'No family matches this search.',
      more: 'Show more',
      add: 'Add',
      adding: 'Adding…',
      added: 'In the library',
    },
    upload: {
      drop: 'Drop the files of one family here, or',
      choose: 'choose them',
      accepted:
        'WOFF2, WOFF, TTF or OTF, up to 6 MB per file. The files of one family (regular, bold, italic...) make a single family.',
      wrongKind: '{name} is not a font file.',
      tooLarge: '{name} is larger than 6 MB.',
      remove: 'Remove {name}',
      size: '{size} kB',
      name: 'Family name',
      namePlaceholder: 'Read from the first file',
      category: 'Kind',
      licence:
        'Check that the font licence allows embedding it in PDF documents and web pages: the fonts of a report travel in every export. A font whose licence forbids it is refused.',
      submit: 'Upload the family',
    },
  },

  tableStyles: {
    title: 'Table styles',
    new: '+ New style',
    empty: 'No table style yet. Create one, then choose it on a table.',
    default: 'Default',
    usage: '{count} tables | {count} table | {count} tables',
    name: 'Name',
    notMine: 'Saved by {owner}: you can look at it and duplicate it, not change it.',
    sample: 'Sample',
    changeNote:
      'Saving changes the {count} table that follows it. | Saving changes the {count} tables that follow it.',
    duplicate: 'Duplicate',
    setDefault: 'Make default',
    unsetDefault: 'Not the default any more',
    delete: 'Delete',
    confirmDelete: 'Confirm deletion',
    save: 'Save the style',
    pick: 'Choose a style in the list, or create one.',
    newName: 'New style',
    copyName: '{name} (copy)',
  },
  houseStyle: {
    title: 'House style',
    settings: 'House style settings',
    preview: 'Preview',
    previewNote: 'The report only changes once the style is applied.',
    firstPage: 'First page of the report',
    tabs: {
      colours: 'Colours',
      type: 'Type',
      tables: 'Tables',
      charts: 'Charts',
    },
    gallery: {
      label: 'House styles',
      report: 'This report',
      current: 'Current look',
      currentNote: 'The report as it is styled today.',
      mine: 'My house styles',
      none: 'Save a house style to find it again in your next reports.',
      loading: 'Loading house styles…',
      shared: 'Shared house styles',
      presets: 'Templates',
      savedOn: 'Saved on {when}',
      by: 'By {owner}',
      remove: 'Delete the house style "{name}"',
      removeYes: 'Delete',
      removeNo: 'Keep',
    },
    presets: {
      classic: { name: 'Classic', note: 'Dark ink, Inter, horizontal rules.' },
      institutional: { name: 'Institutional', note: 'Navy, serif headings, filled table heads.' },
      editorial: { name: 'Editorial', note: 'Cream paper, burgundy, tables barely ruled.' },
      modern: { name: 'Modern', note: 'Teal, ragged text, banded rows.' },
      sustainability: {
        name: 'Sustainability',
        note: 'Forest green, Lora headings, charts in greens.',
      },
      sober: {
        name: 'Sober',
        note: 'Black on white, fully ruled, compact: made for the photocopier.',
      },
    },
    colours: {
      paper: 'Paper',
      paperHint: 'White, or cream like a printed annual report.',
      accent: 'Main colour',
      accentHint: 'Rules, table heads, key figures and, if you wish, headings.',
      palette: 'House colours',
      paletteHint:
        'Offered in the text palette and, if you choose, in charts. Up to {max} besides the main colour.',
      main: 'Main colour',
      colour: 'Colour {n}',
      hex: 'Colour {n} in hexadecimal',
      remove: 'Remove this colour',
      add: '+ Add a colour',
      suggest: 'Suggest from the main colour',
      headings: 'Headings',
      accentHeadings: 'Headings in the main colour',
    },
    type: {
      body: 'Text typeface',
      bodyHint: 'The one the report is set and exported in.',
      heading: 'Heading typeface',
      headingHint:
        'Serif headings over sans text, or the other way round: the commonest pairing a house style makes.',
      sameAsBody: 'Same as the text',
      headingSample: 'Annual report',
      size: 'Text size',
      align: 'Alignment',
      textStyles:
        '{count} text style of the report is saved with the house style. | {count} text styles of the report are saved with the house style.',
    },
    tables: {
      head: 'Table head',
      headHint: 'How the column names are drawn. A table set apart keeps its own.',
      more: 'Reading',
    },
    charts: {
      colours: 'Chart colours',
      coloursHint: 'A series you gave a colour of its own keeps it.',
      shades: 'Shades of the main colour',
      shadesNote: 'One colour, readable in black and white.',
      palette: 'House colours',
      paletteNote: 'The main colour, then the others, in order.',
      paletteEmpty: 'First add colours to the house style, in the Colours tab.',
    },
    save: {
      name: 'House style name',
      placeholder: 'House style name',
      saveNew: 'Save the house style',
      saveCopy: 'Save as new',
      update: 'Update',
      saved: 'House style saved: it is waiting for you in your next reports.',
      updated: 'House style updated.',
    },
    unchanged: 'The report already has this style.',
    changes:
      '{count} setting of the report will change | {count} settings of the report will change',
    locked: 'Read-only report: the house style can be saved, not applied.',
    apply: 'Apply to the report',
    start: {
      label: 'House style',
      hint: 'The style the report starts in. The last one chosen is kept for the next.',
      none: 'None (default look)',
    },
  },

  methods: {
    full: 'Full consolidation',
    proportionate: 'Proportionate consolidation',
    equity: 'Equity method',
  },

  sectionFields: {
    scope: {
      entity: 'Entity',
      country: 'Country',
      method: 'Method',
      control: '% control',
      interest: '% interest',
    },
  },

  esrs: {
    standards: {
      ESRS2: 'General disclosures',
      E1: 'Climate change',
      E2: 'Pollution',
      E3: 'Water and marine resources',
      E4: 'Biodiversity and ecosystems',
      E5: 'Resource use and circular economy',
      S1: 'Own workforce',
      S2: 'Workers in the value chain',
      S3: 'Affected communities',
      S4: 'Consumers and end-users',
      G1: 'Business conduct',
    },
    pillars: {
      general: 'General',
      environment: 'Environment',
      social: 'Social',
      governance: 'Governance',
    },
    materialities: {
      material: 'Material',
      notMaterial: 'Not material',
      phasedIn: 'Phased in',
      byReference: 'By reference',
    },
    materialityHints: {
      material: 'A material topic, disclosed here.',
      notMaterial:
        'Assessed and found not material. That is a disclosure in its own right: leaving a topic out without saying so is not.',
      phasedIn: 'Disclosure deferred under the transitional provisions.',
      byReference: 'Disclosed elsewhere in the report, by reference.',
    },
  },

  inspector: {
    layer: {
      note: 'Elements go anywhere on the page, even past the bleed. The edges, the bleed, the margins, the middle and the other elements act as magnets: hold Alt to ignore them. Shift and a click select several, and an image can be dropped straight onto the page.',
      add: 'Add',
      adding: {
        text: 'Text',
        image: 'Image',
        chart: 'Chart',
        rectangle: 'Rectangle',
        ellipse: 'Ellipse',
        line: 'Line',
      },
      magnets: 'Magnets',
      magnetsHint: 'Page edges, bleed, margins, middle and other elements.',
      none: 'No elements on this page yet.',
      tabs: {
        label: 'Free placement',
        add: 'Add',
        layers: 'Layers',
        element: 'Element',
        arrange: 'Position',
      },
      addOne: 'Add an element',
      showLayers: 'Show the layers',
      nothingTitle: 'No element selected',
      nothingText: 'Click an element on the page, or pick it from the layers.',
      manyText: 'These are set one element at a time. To line them up together, use Position.',
      toArrange: 'Line them up',
      chart: {
        name: 'Name',
      },
      layersHint:
        'The first in the list stands in front of the others: drag a row to restack. Shift and a click select several, and dragging one of them moves them all.',
      drag: 'Drag to restack',
      kinds: {
        text: 'Text',
        image: 'Image',
        chart: 'Chart',
        rectangle: 'Rectangle',
        ellipse: 'Ellipse',
        line: 'Line',
      },
      untitledText: 'Empty text',
      forward: 'Bring forward',
      backward: 'Send backward',
      duplicate: 'Duplicate',
      remove: 'Remove',
      position: 'Position and size',
      x: 'X',
      y: 'Y',
      width: 'W',
      height: 'H',
      alignOnPage: 'Align to the page',
      alignments: {
        left: 'Left edge',
        centre: 'Centre across',
        right: 'Right edge',
        top: 'Top edge',
        middle: 'Centre down',
        bottom: 'Bottom edge',
      },
      opacity: 'Opacity',
      rotation: 'Rotation',
      upright: 'Straighten',
      together: '{count} elements selected',
      togetherHint: 'They move together on the page. Shift and a click adds or removes one.',
      alignTogether: 'Align with each other',
      spread: 'Distribute',
      spreads: {
        x: 'Across',
        y: 'Down',
      },
      removeAll: 'Remove all {count}',
      noColour: 'No colour',
      customColour: 'Another colour',
      colours: {
        accent: 'Accent',
        ink: 'Ink',
        white: 'White',
        grey: 'Grey',
        navy: 'Navy',
        teal: 'Teal',
        sand: 'Sand',
      },
      text: {
        size: 'Size',
        padding: 'Inner margin',
        align: 'Alignment',
        aligns: {
          left: 'Left',
          center: 'Centred',
          right: 'Right',
          justify: 'Justified',
        },
        colour: 'Text',
        fill: 'Background',
      },
      image: {
        picker: 'Image',
        fit: 'Fit',
        fits: {
          cover: 'Fill',
          contain: 'Whole',
        },
        radius: 'Rounding',
      },
      shape: {
        form: 'Shape',
        fill: 'Fill',
        stroke: 'Outline',
        strokeWidth: 'Thickness',
        radius: 'Rounding',
      },
    },

    pageBreak: {
      note: 'A new page starts here. The background below dresses that page.',
      spread: 'Double page',
      spreadHint:
        'The page that starts here spans two facing pages. It exports as one page twice as wide.',
      background: 'Page background',
      backgroundHint: 'Covers the whole page, margins included.',
      fit: 'Fit',
      fitHint: 'How the picture sits on the page.',
      fits: {
        cover: 'Fill',
        contain: 'Whole',
        stretch: 'Stretch',
        tile: 'Tile',
      },
      strength: 'Strength',
      strengthHint: 'A page carrying text wants a quiet background.',
      strengths: {
        full: 'Full',
        soft: 'Soft',
        faint: 'Faint',
      },
      bleedNote: 'Bleed is set in the layout settings, for the whole document.',
    },
    fixedPage: {
      handleHint: 'Click to pick, double-click to rewrite',
      empty: 'This page has no text and no background.',
      source: 'Page {page} of {file}',
      frame: 'Picked text',
      asPrinted: 'As printed',
      rewritten: 'Rewritten',
      pickHint:
        'Click a block of text on the page to pick it; double-click it to rewrite it there.',
      rewrite: 'Rewrite on the page',
      restore: 'Back to the print',
      restoreAll: 'Put every rewritten block back as printed ({count})',
      missing: "Not in the document's fonts, so drawn in a stand-in: {characters}.",
      fixFonts: 'Fix the fonts',
      outline: 'Outline the blocks of text',
      fonts: 'Fonts on this page',
      manageFonts: 'Manage fonts',
      overflow:
        'The text runs {lines} line past its block. Shorten it, or go back to the print. | The text runs {lines} lines past its block. Shorten it, or go back to the print.',
      oversetHint: 'Text runs past this block',
    },
    imagePage: {
      note: 'A single picture, filling its own page. Nothing else appears on it: no title, no text, no neighbouring page.',
      image: 'Image',
      imageHint: 'Fills the whole page, edge to edge.',
      spread: 'Two-page spread',
      spreadHint:
        'For a picture designed across two facing pages. On screen it shows at full width; on export, its left and right halves print on two consecutive pages.',
    },
    tag: {
      label: 'ESRS disclosure',
      hint: 'The reference this section answers: E1-6, S1-14, GOV-1. The index and the coverage check follow from it.',
      notARef: 'That is not an ESRS reference.',
      materiality: 'Materiality',
      clear: 'Remove',
    },

    directions: {
      label: 'Direction',
      clear: 'Remove',
      open: 'Open to everyone who can edit the report. Give it to a direction to keep it to them.',
      inherited: 'Follows its chapter: {names}.',
      ownerOnly: 'Only the owner of the report and an administrator can change this.',
    },

    settings: 'Section settings',
    nothingSelected: 'Nothing selected',
    idle: 'Choose a section in the outline or on the page to change it.',
    locked: 'This report is being reviewed, so its content cannot be changed.',
    textOnPage: 'Write directly on the page. Select some words to format them.',
    pageBreakNothing:
      'A page break has nothing to set. Move it in the outline to change where it falls.',
    deselect: 'Deselect',
    duplicateSection: 'Duplicate this section',
    deleteSection: 'Delete this section',

    chart: {
      title: 'Title',
      type: 'Type',
      data: 'Data',
      series: 'Series',
      label: 'Category',
      labelPlaceholder: 'Category {number}',
      seriesPlaceholder: 'Series',
      removeLabel: 'Remove this category',
      removeSeries: 'Remove this series',
      height: 'Height',
      heightHint: '{mm} mm on the page',
      legend: 'Show the legend',
      caption: 'Caption',
      types: {
        bar: 'Bars',
        line: 'Line',
        pie: 'Pie',
        doughnut: 'Doughnut',
      },
      orientation: 'Bars',
      orientations: {
        vertical: 'Standing',
        horizontal: 'Lying',
      },
      seriesColour: 'Colour of this series',
      labelColour: 'Colour of this category',
      colourEach: 'A colour for each bar',
      accentColours: 'Back to the accent colours',
      stacked: 'Stack the series',
      values: 'Print the figures on the chart',
      axis: 'Show the value scale',
      unit: 'Unit',
      unitHint: 'Printed after every figure: %, GWh, M€.',
      centre: 'In the middle',
      centreHint: 'Words set in the hole: the total, usually.',
      legendPosition: 'Legend',
      tabs: {
        label: 'Chart settings',
        data: 'Data',
        look: 'Look',
      },
      legendPositions: {
        auto: 'Auto',
        top: 'Top',
        right: 'Right',
        bottom: 'Bottom',
        left: 'Left',
      },
    },

    note: {
      number: 'Number',
      numberHint: 'Typed, never recalculated: a note that is cited has to keep its number.',
      title: 'Heading',
      bodyOnPage: 'Write the body of the note directly on the page.',
    },

    scope: {
      title: 'Title',
      entityPlaceholder: 'Company name',
      countryPlaceholder: 'Country',
      methodPlaceholder: 'Method',
      removeEntry: 'Remove this entity',
      addEntry: 'Add an entity',
      caption: 'Caption',
    },

    glossary: {
      title: 'Title',
      termPlaceholder: 'Term',
      definitionPlaceholder: 'Definition',
      removeEntry: 'Remove this term',
      addEntry: 'Add a term',
    },

    divider: {
      variant: 'Type',
      space: 'Height',
      spaceHint: '{mm} mm of white space',
      variants: {
        rule: 'Rule',
        space: 'Space',
      },
    },

    datapoint: {
      label: 'Label',
      labelPlaceholder: 'Gross GHG emissions, scope 1',
      value: 'Value',
      unit: 'Unit',
      unitHint:
        'Beside the figure, never inside it: a unit typed into the value makes it unreadable to everything downstream.',
      unitPlaceholder: 'tCO2e',
      period: 'Period',
      periodPlaceholder: '2026',
      comparative: 'Comparative',
      comparativePeriod: 'Comparative period',
      comparativePeriodPlaceholder: '2025',
      method: 'Method',
      methodHint:
        'Boundary, how it was calculated, estimates, exclusions. This is what the assurance provider reads.',
    },

    target: {
      label: 'Label',
      labelPlaceholder: 'Reduction in scope 1 and 2 emissions',
      unit: 'Unit',
      unitPlaceholder: 'tCO2e',
      base: 'Base',
      baseValue: 'Base value',
      baseYear: 'Base year',
      goal: 'Target',
      targetValue: 'Target value',
      targetYear: 'Target year',
      current: 'Today',
      currentValue: 'Current value',
      currentHint:
        'Progress is worked out from the three figures, never typed, so it cannot disagree with them.',
      regressing: 'Going backwards',
      note: 'Basis',
      noteHint: 'Absolute or intensity, the scopes covered, whether the target is science based.',
    },

    materiality: {
      impact: 'Impact',
      financial: 'Financial',
      impactAxis: 'Impact materiality →',
      financialAxis: '↑ Financial materiality',
      topicPlaceholder: 'Topic',
      addTopic: 'Add a topic',
      removeTopic: 'Remove this topic',
      threshold: 'Threshold',
      thresholdHint: 'The line above which a topic is taken as material, on both axes.',
      caption: 'Caption',
    },

    iro: {
      topic: 'Topic',
      topicPlaceholder: 'Topic',
      type: 'Type',
      position: 'Value chain',
      horizon: 'Horizon',
      conclusion: 'Conclusion',
      conclusionPlaceholder: 'What was concluded',
      types: {
        impact: 'Impact',
        risk: 'Risk',
        opportunity: 'Opportunity',
      },
      positions: {
        upstream: 'Upstream',
        own: 'Own operations',
        downstream: 'Downstream',
      },
      horizons: {
        short: 'Short term',
        medium: 'Medium term',
        long: 'Long term',
      },
      addEntry: 'Add a row',
      removeEntry: 'Remove this row',
      empty: 'No impact, risk or opportunity entered yet.',
      caption: 'Caption',
    },

    risks: {
      title: 'Risk',
      titlePlaceholder: 'Name of the risk',
      category: 'Risk family',
      categoryPlaceholder: 'Operational risks',
      level: 'Severity',
      levels: {
        high: 'High',
        medium: 'Moderate',
        low: 'Low',
      },
      description: 'Description',
      descriptionPlaceholder: 'What could happen, and what it would do',
      management: 'How it is managed',
      managementPlaceholder: 'The controls in place',
      addEntry: 'Add a risk',
      removeEntry: 'Remove this risk',
      empty: 'No risk entered yet.',
      uncategorised: 'Other risks',
      caption: 'Caption',
    },

    ghg: {
      tabs: {
        label: 'Inventory settings',
        lines: 'Emissions',
        settings: 'Settings',
      },
      unit: 'Unit',
      period: 'Year',
      comparativePeriod: 'Comparative year',
      line: 'Item',
      linePlaceholder: 'What this line is',
      category: 'Category',
      addLine: 'Add a line',
      removeLine: 'Remove this line',
      intensityLabel: 'Intensity',
      intensityHint:
        'A total that falls while the business doubles is not a reduction. Intensity is worked out from the total and the denominator.',
      intensityPlaceholder: 'tCO2e per \u20acm of turnover',
      intensityValue: 'Denominator',
      intensityComparative: 'Comparative denominator',
      caption: 'Caption',
      scopes: {
        scope1: 'Scope 1 \u2013 direct emissions',
        scope2Location: 'Scope 2 \u2013 location based',
        scope2Market: 'Scope 2 \u2013 market based',
        scope3: 'Scope 3 \u2013 other indirect emissions',
      },
      subtotals: {
        scope1: 'Total scope 1',
        scope2Location: 'Total scope 2 (location)',
        scope2Market: 'Total scope 2 (market)',
        scope3: 'Total scope 3',
      },
      totalMarket: 'Total (market based scope 2)',
      totalLocation: 'Total (location based scope 2)',
      categories: {
        '1': 'Purchased goods and services',
        '2': 'Capital goods',
        '3': 'Fuel and energy related activities not in scopes 1 and 2',
        '4': 'Upstream transportation and distribution',
        '5': 'Waste generated in operations',
        '6': 'Business travel',
        '7': 'Employee commuting',
        '8': 'Upstream leased assets',
        '9': 'Downstream transportation and distribution',
        '10': 'Processing of sold products',
        '11': 'Use of sold products',
        '12': 'End of life treatment of sold products',
        '13': 'Downstream leased assets',
        '14': 'Franchises',
        '15': 'Investments',
      },
      problems: {
        unreadable:
          '{count} figure could not be read and is in no total. | {count} figures could not be read and are in no total.',
        noScope2Basis:
          'Scope 2 is reported on one basis only. Both are expected, and reporting one without saying so is the commonest omission there is.',
        categoryTwice: 'Category "{category}" appears twice: it is counted twice in the total.',
      },
    },

    taxonomy: {
      kpi: 'KPI',
      kpis: {
        turnover: 'Turnover',
        capex: 'CapEx',
        opex: 'OpEx',
      },
      total: 'Total',
      eligible: 'Eligible',
      eligibleShare: 'Eligible share',
      aligned: 'Aligned',
      alignedShare: 'Aligned share',
      unit: 'Unit',
      unitPlaceholder: 'In thousands of euros',
      period: 'Year',
      caption: 'Caption',
      impossible:
        'The aligned amount exceeds the eligible amount. An activity has to be eligible before it can be aligned, so one of the two figures is wrong.',
    },

    index: {
      lead: 'The index is built from the ESRS references the sections carry, the way the contents page is built from the headings.',
      showTitles: 'Show section titles',
      showTitlesHint: 'The title of the section pointed at, beside its reference.',
      empty: 'No section carries an ESRS reference yet.',
      check: 'Coverage check',
      noGaps: 'Nothing to report.',
      gaps: {
        declaredNotDisclosed:
          '{standard} is declared material, but no requirement of that standard is disclosed.',
        duplicated: 'The reference {ref} is claimed by two sections.',
      },
    },

    cover: {
      arrangement: 'Arrangement',
      variants: {
        classic: 'Classic',
        centred: 'Centred',
        band: 'Band',
        portrait: 'Picture',
        framed: 'Framed',
        sidebar: 'Sidebar',
        full: 'Full page',
      },
      logo: 'Logo',
      logoHint:
        'A wordmark, ideally on a transparent ground. On a coloured band it is printed reversed.',
      picture: 'Picture',
      pictureHint:
        'A photograph or an illustration. It is cropped to fill whatever room the arrangement gives it.',
      note: 'Foot line',
      noteHint: 'A reference, a version, a confidentiality note.',
      notePlaceholder: 'Confidential \u2013 restricted circulation',
      entity: 'Entity',
      entityPlaceholder: 'Company name',
      subtitle: 'Subtitle',
      titleStyle: 'Text style, title',
      subtitlePlaceholder: 'Annual financial statements',
      period: 'Period',
      periodPlaceholder: 'Year ended 31 December 2026',
      standard: 'Standard',
      standardPlaceholder: 'IFRS',
      standardHint: 'Shown above the title, in small capitals.',
    },

    group: {
      name: 'Name',
      showTitle: 'Print the name as a heading',
      note: 'Turn this off for a group used only to organise the outline. It still holds its sections; it just does not appear in the document.',
      columns: 'Columns',
      columnsHint:
        'How many sections sit side by side. The document order does not change: left to right, then down.',
      perLine: '{count} per line',
      align: 'Alignment',
      alignHint: 'How sections of different heights sit against each other on one line.',
      alignments: {
        stretch: 'Equal height',
        start: 'Top',
        center: 'Centred',
      },
      count: '{count} inside',
    },

    heading: {
      text: 'Text',
      level: 'Level',
      levelHint: 'Levels 1 to 3 appear on the contents page.',
      levelGroup: 'Heading level',
      inContents: 'In the contents',
      listed: 'Listed in the contents page',
      contentsLabel: 'Line in the contents',
      contentsLabelHint: 'What the contents says in place of the title. Empty: the title.',
      contentsDetail: 'Line under the entry',
      contentsDetailHint: 'A few words under the entry. The pages it runs are added by themselves.',
      chapterColour: 'Chapter colour',
      chapterColourAuto: 'Next colour of the palette',
      houseColour: 'House colour {number}',
      textStyleHint: "Set in one of the document's text styles rather than the heading style.",
    },

    text: {
      tabs: {
        label: 'Text settings',
        format: 'Format',
        layout: 'Layout',
      },
      columns: 'Columns',
      columnsHint:
        'Two or three columns flow the same text from one into the next, like a newspaper.',
      columnCounts: {
        1: '1 column',
        2: '2 columns',
        3: '3 columns',
      },
      textStyle: 'Text style',
      textStyleHint:
        'Applies to the whole block. A word coloured from the toolbar, inside it, keeps its own colour.',
      textStyleNone: 'No style',
      layout: {
        gap: 'Space between columns',
        gaps: {
          narrow: 'Narrow',
          normal: 'Normal',
          wide: 'Wide',
        },
        rule: 'A line between the columns',
        align: 'Alignment',
        aligns: {
          document: 'Document',
          left: 'Left',
          justify: 'Justified',
          center: 'Centred',
        },
        leading: 'Line spacing',
        leadings: {
          document: 'Document',
          tight: 'Tight',
          normal: 'Normal',
          loose: 'Airy',
        },
        frame: 'Frame',
        frameHint: 'Sets the block apart: a tint behind it, a line round it, or the accent tint.',
        frames: {
          none: 'None',
          tint: 'Tint',
          outline: 'Outline',
          accent: 'Accent',
        },
        hyphens: 'Hyphenate words at line ends',
        dropCap: 'Drop cap on the first letter',
      },
      format: {
        idle: 'Click in the text on the page, then choose here: the formatting applies to what is selected.',
        style: 'Style',
        characters: 'Characters',
        size: 'Size',
        sizes: {
          small: 'Small',
          normal: 'Normal',
          large: 'Large',
          xlarge: 'Larger',
        },
        ink: 'Text colour',
        inks: {
          auto: 'Automatic',
          accent: 'Accent',
          ink: 'Ink',
          grey: 'Grey',
          red: 'Red',
          orange: 'Orange',
          green: 'Green',
          teal: 'Teal',
          blue: 'Blue',
          violet: 'Violet',
          house: 'House colour {n}',
        },
        houseInks: 'House',
        highlight: 'Highlight',
        highlights: {
          none: 'None',
          yellow: 'Yellow',
          green: 'Green',
          blue: 'Blue',
          pink: 'Pink',
          grey: 'Grey',
        },
        paragraph: 'Paragraph',
        align: 'Alignment',
        aligns: {
          left: 'Left',
          center: 'Centred',
          right: 'Right',
          justify: 'Justified',
        },
        lists: 'Lists and quotation',
        paragraphTools: {
          bullets: 'Bulleted list',
          numbers: 'Numbered list',
          outdent: 'Decrease indent',
          indent: 'Increase indent',
          quote: 'Quotation',
        },
        link: 'Link',
        linkPlaceholder: 'https://',
        linkApply: 'Apply',
        linkRemove: 'Remove',
      },
    },

    table: {
      tabs: {
        label: 'Table settings',
        cells: 'Cells',
        look: 'Look',
        settings: 'Settings',
      },
      title: 'Title',
      caption: 'Caption',
      captionHint: 'Printed under the table, for a source or a basis.',
      fit: 'Width',
      fitHint:
        'How the table sits on the page. “Fit to page” scales the whole table down to the page width, as Excel does.',
      fitOptions: {
        normal: 'Normal',
        shrink: 'Fit to page',
        compact: 'Compact',
      },
      columnWidths: 'Column widths',
      columnWidthsHint: 'Percentages of the table width. Left empty, columns follow their content.',
      columnsNarrow:
        '{count} column in orange is too narrow for its words, which break letter by letter: widen it, or share the widths equally. | {count} columns in orange are too narrow for their words, which break letter by letter: widen them, or share the widths equally.',
      columnWidthsSet: 'Set widths',
      columnWidthsEqual: 'Share equally',
      columnWidthsAuto: 'Auto (content)',
      style: {
        title: 'Style',
        none: 'No style (the document only)',
        byDefault: 'Default: {name}',
        library: 'Style library...',
        adjusted:
          '{count} setting adjusted on this table | {count} settings adjusted on this table',
        missing: 'The style this table followed is no longer in the library.',
        revert: 'Back to the style',
        revertAll: 'Back to the style',
      },
      look: {
        title: 'Look',
        reset: 'Use the document style',
        document: 'Document',
        head: 'Header',
        heads: {
          underline: 'Underlined',
          tinted: 'Tinted',
          filled: 'Filled',
          plain: 'Plain',
        },
        colour: 'Colour',
        documentColour: "The document's accent",
        customColour: 'Another colour',
        lines: 'Lines',
        rows: 'Row height',
        banded: 'Banded rows',
        labels: 'First column as labels, in bold',
        total: 'Last row as the total',
        negatives: 'Negative figures',
        negativesHint:
          'Only how they are shown: the figure keeps its sign in the table and in CSRD.',
        negativeStyles: {
          minus: '-1,467',
          parentheses: '(1,467)',
        },
        headCase: 'Header words',
        headCases: { normal: 'As written', upper: 'CAPITALS' },
        tint: 'Tint of the band',
        derivedTint: 'Derived from the colour',
        totals: 'Total rows',
        totalsHint: 'The rows with a bold cell, which CSRD sets for its totals.',
        totalsStyles: { fill: 'Tinted band', bold: 'Bold only' },
      },
      cells: 'Cells',
      row: 'Row',
      column: 'Column',
      columnPlaceholder: 'Column {number}',
      markNumeric: 'Treat this column as figures',
      selectTable: 'Select the whole table',
      selectHeader: 'Select the header row',
      selectRow: 'Select row {number}',
      selectColumn: 'Select column {number}',
      headerHandle: 'H',
      selectHint:
        'Click a cell, a row number or a column number to set it. Drag across several cells, or Shift+click, to pick a block to merge.',
      selection: {
        cell: 'Cell, row {row}, column {column}',
        headerCell: 'Header, column {column}',
        row: 'Row {row}',
        header: 'Header row',
        column: 'Column {column}',
        range: 'Block of {rows} × {columns}',
        table: 'The whole table',
      },
      deselect: 'Deselect',
      align: 'Alignment',
      aligns: {
        left: 'Left',
        center: 'Centred',
        right: 'Right',
      },
      strong: 'Emphasised',
      strongHint: 'For a total or a subtotal: bold, with a rule above.',
      ruleAbove: 'Rule above',
      ruleAboveHint: 'A rule along the top of the selected cells, in the table colour.',
      ruleBelow: 'Rule below',
      ruleBelowHint: 'A rule along the bottom of the selected cells, in the table colour.',
      numeric: 'Figures',
      fill: 'Background',
      ink: 'Text',
      noColour: 'No colour',
      customColour: 'Another colour',
      fills: {
        grey: 'Grey',
        blue: 'Blue',
        green: 'Green',
        amber: 'Amber',
        rose: 'Pink',
        teal: 'Teal',
      },
      inks: {
        ink: 'Ink',
        grey: 'Grey',
        navy: 'Navy',
        green: 'Green',
        red: 'Red',
      },
      rows: 'Rows',
      columns: 'Columns',
      rowAbove: 'Row above',
      rowBelow: 'Row below',
      columnLeft: 'Column to the left',
      columnRight: 'Column to the right',
      moveUp: 'Move up',
      moveDown: 'Move down',
      moveLeft: 'Move left',
      moveRight: 'Move right',
      deleteRow: 'Delete the row',
      deleteColumn: 'Delete the column',
      clear: 'Clear',
      clearHint: 'Empties the text and the settings of what is selected.',
      openEditor: 'Open the table editor',
      openEditorHint:
        'Full size, with a preview of the table as it will print. Double-clicking the table on the page opens it too.',
      editorTitle: 'Table: {title}',
      editorTitleBare: 'Table',
      preview: 'Preview, as printed',
      previewHint: 'Click a cell to select it, Shift+click for a block.',
      merging: 'Merge',
      merge: 'Merge cells',
      mergeHint: 'Makes the block a single cell, which keeps the first text.',
      mergeLoses:
        'The first text is kept and {count} other is removed. | The first text is kept and the other {count} are removed.',
      split: 'Split',
      splitHint: 'Gives every cell back its place. The text stays in the first one.',
      tiedRow: 'This row is part of a merged cell: split it to move the row.',
      tiedColumn: 'This column is part of a merged cell: split it to move the column.',
    },

    kpi: {
      title: 'Title',
      labelPlaceholder: 'Revenue',
      valuePlaceholder: '128.4M',
      changePlaceholder: '+4.2%',
      removeFigure: 'Remove this figure',
      addFigure: 'Add a figure',
      toneGroup: 'How the movement reads',
      neutral: 'Neutral',
      good: 'Good',
      poor: 'Poor',
    },

    image: {
      choose: 'Choose an image, or drop one here',
      replace: 'Replace',
      remove: 'Remove',
      file: 'File',
      caption: 'Caption',
      width: 'Width',
      widthHint: '{percent}% of the text width',
      notAnImage: 'That is not an image.',
      tooLarge: 'That image is larger than 4 MB. Reduce it and try again.',
      unreadable: 'That file could not be read.',
    },

    contents: {
      title: 'Title',
      depth: 'Depth',
      depthHint: 'How many levels of heading to list.',
      subtitle: 'Subtitle',
      pageNumbers: 'Page numbers',
      showDetail: 'Line under each entry',
      levelStyle: 'Text style, level {level}',
      levelStyleHint:
        'The document text styles, one for each level: change the style and every contents page follows.',
      levelStyleNone: 'As the page sets it',
      note: 'The list is worked out from the document each time it is drawn, so it never goes stale.',
    },

    signatures: {
      statement: 'Statement',
      statementHint: 'Printed above the signatures.',
      statementPlaceholder:
        'The financial statements were approved by the board and signed on its behalf.',
      namePlaceholder: 'Name',
      rolePlaceholder: 'Role',
      placePlaceholder: 'Place and date',
      removeSignatory: 'Remove this signatory',
      addSignatory: 'Add a signatory',
    },
  },

  workflow: {
    // Left in French: these are the terms the business uses, and they appear
    // on the document itself.
    states: {
      brouillon: 'Brouillon',
      en_revue: 'En revue',
      valide: 'Validé',
      diffuse: 'Diffusé',
      rejete: 'Rejeté',
    },
    actions: {
      brouillon: 'Reopen for editing',
      en_revue: 'Submit for review',
      valide: 'Approve',
      diffuse: 'Issue',
      rejete: 'Send back',
    },
  },

  fonts: {
    title: 'Fonts',
    leadNeeding:
      '{count} font is not complete: what was printed shows exactly, new characters use a stand-in. | {count} fonts are not complete: what was printed shows exactly, new characters use a stand-in.',
    leadComplete: 'Every font is complete: any text can be written in them.',
    none: 'No imported page uses a font of its own yet.',
    sample: 'Results 2025: revenue up 12%, margin up?!',
    meta: 'Weight {weight}',
    italic: 'italic',
    characters: '{count} character from the PDF | {count} characters from the PDF',
    status: {
      complete: 'Complete',
      partial: 'From the PDF',
      missing: 'Missing',
    },
    explain: {
      complete: 'The whole font is available: every character, typed or printed, is drawn in it.',
      partial:
        'Taken from the PDF, which only carried the characters it printed. Anything else is drawn in {substitute} until the font file is uploaded.',
      missing:
        'The PDF did not carry this font. Everything in it is drawn in {substitute}: upload the font file to show it as printed.',
    },
    missingInSample: 'Drawn in the stand-in here: {characters}',
    upload: 'Upload the font file',
    replaceFile: 'Use another file',
    removeFile: 'Remove the file',
    replaceWith: 'Stand-in',
    reading: 'Reading…',
    problems: {
      kind: 'Choose a .woff2, .woff, .ttf or .otf file.',
      size: 'This file is too large to be a single font.',
      unreadable: 'This file could not be read as a font.',
    },
  },
  importing: {
    selectAll: 'Take everything',
    selectNone: 'Take nothing',

    rows: '{count} row | {count} rows',
    columns: '{count} column | {count} columns',
    paragraphs: '{count} paragraph | {count} paragraphs',

    pdf: {
      title: 'Import a PDF',
      subtitle: 'As pages of the report, or exactly as printed',
      steps: {
        file: 'File',
        analysis: 'Analysis',
        pages: 'Pages',
        fonts: 'Fonts',
      },
      lead: "Choose last year's report. Its pages come back as the report's own sections, headings, text, tables and pictures in its fonts and styles, or exactly as printed.",
      choose: 'Choose a PDF',
      uploading: 'Sending the file…',
      wrongType: 'Choose a .pdf file.',
      unreadable: 'This file could not be read as a PDF.',
      stages: {
        reading: 'Reading the pages',
        tables: 'Reading the tables',
        interpreting: 'Recognising the structure',
        fonts: 'Recovering the fonts',
        pictures: 'Drawing the backgrounds',
      },
      stageHints: {
        reading: 'Every word with its font, size, colour and position.',
        tables: 'Tables drawn without lines, put back into rows and columns.',
        interpreting: 'Titles, paragraphs, lists, and what sits apart from the text.',
        fonts: 'The fonts the PDF carries, made usable again.',
        pictures: 'Each page without its text, and its pictures lifted off it.',
      },
      counted: '{done} of {total}',
      live: {
        title: 'What the import has found so far',
        page: 'Page {page}',
        waiting: 'Waiting',
        read: 'Read',
        flowing: 'Document, {count} column | Document, {count} columns',
        designed: 'Free layout',
        looked: 'Seen by AI',
        kinds: {
          heading: 'Heading',
          text: 'Text',
          table: 'Table',
          chart: 'Chart',
          picture: 'Picture',
          free: 'Placed freely',
          running: 'Running head or foot',
        },
      },
      chosenCount: '{count} page of {total} chosen | {count} pages of {total} chosen',
      all: 'All',
      none: 'None',
      range: 'Pages to choose',
      rangePlaceholder: '1-12, 15',
      apply: 'Choose',
      pagesHints: {
        components:
          'Each page opens a page of the report: its running text as headings, text and tables, everything else placed freely over its background.',
        exact: 'Each page becomes a page of the report, redrawn word for word, on its own sheet.',
      },
      modes: {
        label: 'How the pages come in',
        recommended: 'Recommended',
        components: {
          name: 'As components',
          hint: "Headings, text, tables and pictures you edit, move and reflow like any page of the report, in the PDF's fonts and styles.",
        },
        exact: {
          name: 'Exact copy',
          hint: 'Every word exactly where it was printed. Text can be rewritten in place, but nothing reflows.',
        },
      },
      layouts: {
        flowing: 'text · {count} column | text · {count} columns',
        designed: 'free layout',
      },
      pageLabel: 'Page {page}',
      scanned: 'scan',
      fontsLead:
        '{complete} of {total} fonts are complete. Decide here what the others should do, or later from the page.',
      matchPaper: "Set the document's paper to the PDF's ({width} × {height})",
      matchLayout:
        "Set the document's paper ({width} × {height}), margins and text size to the PDF's",
      fontsUrge: {
        title: '{count} font is not complete | {count} fonts are not complete',
        body: 'Text imported as components is meant to be rewritten, and a font the PDF carried only in part draws every character it lacks in its stand-in. Upload the real font files for text that looks right as it is edited.',
      },
      confirmFonts:
        '{count} font is still incomplete: rewritten text will mix in its stand-in. Upload it first, or import anyway. | {count} fonts are still incomplete: rewritten text will mix in their stand-ins. Upload them first, or import anyway.',
      importAnyway: 'Import anyway',
      toFonts: 'Next: fonts',
      import: 'Import {count} page | Import {count} pages',
      importing: 'Importing…',
      pageTitle: 'Page {page} of {file}',
      layerTitle: 'Placed on page {page}',
      textTitle: 'Text, page {page}',
      tableTitle: 'Table, page {page}',
      chartTitle: 'Chart, page {page}',
      spaceTitle: 'Space, page {page}',
      styleName: '{role}, {size} pt',
      roles: {
        title: 'Title',
        h1: 'Heading 1',
        h2: 'Heading 2',
        h3: 'Heading 3',
        body: 'Body',
        bullet: 'List',
        caption: 'Caption',
        note: 'Note',
        key_figure: 'Key figure',
        table_header: 'Table header',
        table_body: 'Table',
        other: 'Text',
      },
    },
    word: {
      title: 'Import from Word',
      lead: 'Headings, paragraphs and tables come across as sections you can reorder and comment on. Images do not.',
      choose: 'Choose a .docx file, or drop one here',
      reading: 'Reading...',
      unreadable: 'That file could not be read. It has to be a .docx.',
      wrongType: 'That has to be a .docx file.',
      nothing: 'Nothing could be read from that document.',
      chosen: '{count} of {total} kept | {count} of {total} kept',
      addChosen: 'Add {count} section | Add {count} sections',
      keepSection: 'Keep this section',
      keepChapter: 'Keep this whole chapter',
      keepParagraph: 'Keep this paragraph',
      showParagraphs: 'Show the paragraphs',
      hideParagraphs: 'Hide the paragraphs',
      keepLooks: "Keep the tables' formatting",
      keepLooksHint: 'Cell backgrounds and colours, bold and alignment, as in the document.',
      pickToPreview: 'Pick something on the left to read the whole of it.',
      droppedParagraph: 'This paragraph will not be imported.',
      imageLeftOut: 'One image was left out. Add it with the Image section.',
      imagesLeftOut: '{count} images were left out. Add them with the Image section.',
      simplified:
        '{count} formatting feature was simplified. | {count} formatting features were simplified.',
    },

    excel: {
      title: 'Import from Excel',
      lead: 'Pick the sheet, then the range of cells to take. Columns of figures are set to align right and totals are picked out.',
      choose: 'Choose a workbook, or drop one here',
      reading: 'Reading...',
      unreadable: 'That file could not be read. It has to be a .xlsx or .xls.',
      wrongType: 'That has to be a .xlsx or .xls file.',
      noTable: 'No sheet in that workbook holds a table. You can still choose a range by hand.',
      sheets: 'Sheets in this workbook',
      range: 'Range',
      badRange: 'No such range',
      rangeSize: '{rows} by {columns}',
      suggested: 'Table found',
      suggestedHint: 'Go back to where the table appears to be.',
      wholeSheet: 'The whole sheet',
      dragHint:
        'Drag across the sheet to choose the range. Click a letter for a whole column, a number for a row, or type the reference, such as C4:H38.',
      beyond: 'A further {rows} and {columns} are not shown. Type their reference to take them.',
      headerRow: 'First row',
      header: {
        auto: 'Best guess',
        firstRow: 'These are headings',
        none: 'These are figures',
      },
      keepLooks: "Keep the workbook's formatting",
      keepLooksHint: 'Cell backgrounds and colours, bold and alignment.',
      fit: 'Width',
      fitHint:
        'For a wide sheet, “Fit to page” scales the whole table down so it stays on the page.',
      fitOptions: {
        normal: 'Normal',
        shrink: 'Fit to page',
        compact: 'Compact',
      },
      takeThisSheet: 'Take this sheet',
      asImported: 'As it will be imported',
      tableSize: '{rows} · {columns}',
      moreRows: 'and {count} more row | and {count} more rows',
      sheetEmpty: 'This sheet is empty.',
      pickRange: 'Choose a range on the sheet.',
      rangeTooShort:
        'Two rows at least. With only one, nothing says whether it is headings or figures.',
      rangeEmpty: 'There is nothing in that range.',
      add: 'Add {count} table | Add {count} tables',
    },

    csrd: {
      title: 'Import from CSRD',
      lead: 'Narratives and tables collected in CSRD come across as sections, each disclosure tagged with the requirement it answers.',
      connecting: 'Reaching CSRD...',
      signIn:
        'Sign in through the portal to reach CSRD. Without it, CSRD cannot tell who is asking.',
      devToken:
        'CSRD refused the development token, which lasts about an hour. Put a fresh one in the token file the API reads (CSRD_DEV_TOKEN_FILE).',
      noClient: 'Your portal account is not attached to a CSRD client.',
      forbidden: 'Your CSRD profile does not allow reading the collection.',
      unreachable: 'CSRD could not be reached. Try again in a moment.',
      failed:
        'CSRD answered with an error while reading this collection. If it happens again, the problem is on the CSRD side.',
      noReporting: 'There is no reporting in CSRD yet.',
      noEntities: 'No entity in CSRD is open to you.',
      signedInAs: 'Signed in as',
      reporting: 'Reporting',
      reportingHint: 'The one selected in CSRD. To read another, change it there.',
      reportingOf: 'Reporting to read',
      principal: 'principal',
      secondary: 'secondary',
      secondaryHint:
        'CSRD will be moved onto this reporting for the length of the reading, then back onto {principal}.',
      otherReporting:
        'CSRD is on reporting {current}, not on the principal one of this report ({principal}).',
      moveTo: 'Move CSRD to {code}',
      moving: 'Moving CSRD...',
      entity: 'Entity',
      loadingList: 'Reading what was collected...',
      nothingListed: 'Nothing is collected for this entity.',
      chosen: '{count} of {total} chosen',
      progress: '{count}% filled in',
      takeStandard: 'Take all of {standard}',
      takeDisclosure: 'Take {name}',
      read: 'Read {count} disclosure | Read {count} disclosures',
      reading: 'Reading...',
      nothingCollected: 'Nothing has been collected in the disclosures chosen.',
      emptyLeftOut:
        '{count} disclosure had nothing collected and was left out. | {count} disclosures had nothing collected and were left out.',
      back: 'Back',
      caption: 'Source: CSRD, {reporting}, {entity}, read on {date}',
    },
  },

  collaboration: {
    comments: 'Comments',
    newComment: 'New comment',
    discussion: 'Discussion',
    noComments:
      'No comments yet. Select a section on the page and use its comment mark to start one.',
    willStart: 'This will start a discussion on the selected section.',
    write: 'Write a comment',
    backToAll: 'Back to all comments',
    closeComments: 'Close comments',
    resolve: 'Resolve',
    reopen: 'Reopen',
    resolved: 'Resolved',
    messageCount: '{count} messages',
    showOnPage: 'Show on page',
    withdrawn: 'Withdrawn',

    share: {
      title: 'Share this report',
      who: 'Who to invite',
      placeholder: 'Choose a person',
      nobody: 'Nobody left to invite. Add people on the Access screen.',
      invite: 'Invite',
      whatTheyCanDo: 'What they can do',
      owner: 'Owner',
      invited: 'Invited',
      removeOne: 'Remove {name}',
      roles: {
        commenter: 'Comment',
        commenterHint: 'Read it and discuss it',
        reviewer: 'Edit',
        reviewerHint: 'Change what it contains',
        validateur: 'Approve',
        validateurHint: 'Sign it off, or send it back',
      },
    },

    remote: {
      saved: '{name} saved a newer version of this report.',
      take: 'Take their version',
      keep: 'Keep mine',
    },
  },

  exporting: {
    title: 'Export',
    pdf: 'PDF',
    pdfHint: 'Exactly as it looks here. For sending and for signing.',
    html: 'HTML',
    htmlHint: 'The same page, as a file. Opens in any browser, nothing to install.',
    xlsx: 'Excel',
    xlsxHint: 'Tables as figures somebody can work with.',
    docx: 'Word',
    docxHint: 'For a report that still has to be edited by hand.',
    working: 'Producing...',
    failed: 'The file could not be produced.',
    note: 'The first PDF after a restart takes a few seconds longer: the server starts a browser to print with, and keeps it for the ones after.',
  },

  theme: {
    toLight: 'Switch to light',
    toDark: 'Switch to dark',
  },

  language: {
    label: 'Language',
    fr: 'Français',
    en: 'English',
  },

  kit: {
    title: 'UI kit',
    subtitle:
      "Quantum's components, and the ones added for the builder in the same style. Every one follows the light and the dark theme.",
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    sections: {
      actions: 'Actions',
      status: 'Status',
      forms: 'Forms',
      feedback: 'Feedback',
      figures: 'Figures',
      navigation: 'Navigation',
      overlays: 'Dialogs and people',
    },
    buttons: {
      primary: 'Export',
      secondary: 'Share',
      soft: 'Add a section',
      ghost: 'Comments',
      link: 'See the history',
      danger: 'Delete',
      small: 'Small',
      large: 'Large',
      pill: 'Filter',
      loading: 'Saving',
      disabled: 'Unavailable',
    },
    tones: {
      neutral: 'Draft',
      blue: 'In progress',
      cyan: 'Imported',
      violet: 'Assisted',
      success: 'Approved',
      warning: 'In review',
      danger: 'Rejected',
    },
    chips: {
      sections: 'sections',
      comments: 'open comments',
      fonts: 'fonts to settle',
      filter: 'Mine only',
    },
    fields: {
      title: 'Report title',
      titleHint: 'Shown on the cover and in the contents.',
      titleValue: 'Annual financial report 2025',
      period: 'Period',
      periods: {
        fy2025: 'Financial year 2025',
        fy2024: 'Financial year 2024',
      },
      entity: 'Reporting entity',
      entityError: 'Name the entity the report is for.',
      note: 'Note for reviewers',
      notePlaceholder: 'What changed since the last version',
      include: 'Include the notes',
      some: 'Some pages chosen',
      outline: 'Outline the blocks of text',
      align: 'Alignment',
      alignments: {
        left: 'Left',
        center: 'Centred',
        right: 'Right',
      },
    },
    callouts: {
      blueTitle: 'Imported as components',
      blueBody: 'Headings, text and tables you can edit and reflow like any page.',
      action: 'Open',
      cyanTitle: '20 pages read',
      cyanBody: '6 flow in columns, 14 are laid out freely.',
      violetTitle: 'Styles named by the assistant',
      warningTitle: '2 fonts are incomplete',
      warningBody: 'Upload the font files before rewriting text.',
      dangerTitle: 'The export did not finish',
      dangerBody: 'The service did not answer. Try again in a moment.',
    },
    spinner: 'Loading',
    meter: 'Progress',
    empty: {
      title: 'Nothing on this page yet',
      body: 'Add a section from the panel, or import a document.',
      action: 'Add a section',
    },
    stats: {
      revenue: 'Revenue',
      billions: 'bn €',
      revenueHint: 'Driven by the new contracts in the Paris region.',
      margin: 'Operating margin',
      emissions: 'Scope 1 and 2 emissions since 2015',
    },
    kicker: 'Step of the import',
    table: {
      caption: 'Key figures',
      description: 'In millions of euros.',
      item: 'Item',
      revenue: 'Revenue',
      ebitda: 'EBITDA',
      net: 'Net income, group share',
    },
    tabs: {
      contents: 'Contents',
      layout: 'Layout',
      comments: 'Comments',
      contentsBody: 'The sections of the report, in order.',
      layoutBody: 'Paper, margins, type and colours.',
      commentsBody: 'What reviewers asked, and what was answered.',
    },
    dialog: {
      open: 'Rename the report',
      title: 'Rename the report',
      description: 'The new name shows in the list of reports.',
      label: 'Name',
      confirm: 'Rename',
    },
  },
}
